/**
 * Feito pelo agente netexperts
 * Módulo: AnalyticsObra — resumo recebido / a receber por contrato de venda
 */
import { useState } from 'react'
import { MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { formatCurrency, formatNumber, formatPercent } from '../../../../utils/obraAnalytics'
import KpiCard from '../../../../Components/Analytics/KpiCard'

const CONTRATOS_POR_PAGINA = 10

const ContratosFinanceiroPanel = ({
    title = 'Resumo financeiro por contrato',
    subtitle = 'Recebido e saldo a receber de cada venda no período (10 por página).',
    pctRecebido = 0,
    pctAReceber = 0,
    contratos = [],
    paginacao = { page: 0, per_page: CONTRATOS_POR_PAGINA, total: 0, total_pages: 0 },
    onSearch,
    onPageChange,
    emptyMessage = 'Sem contratos no período.',
}) => {
    const [buscaInput, setBuscaInput] = useState('')

    const aplicarBusca = () => onSearch?.(buscaInput.trim())
    const limparBusca = () => { setBuscaInput(''); onSearch?.('') }

    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
            <div>
                <h3 className="text-base font-semibold text-gray-800">{title}</h3>
                <p className="text-xs text-gray-500">{subtitle}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <KpiCard title="% Recebido" value={formatPercent(pctRecebido)} subtitle="Sobre o VGV vendido no período" />
                <KpiCard title="% A Receber" value={formatPercent(pctAReceber)} subtitle="Saldo pendente dos contratos" />
            </div>

            {onSearch && (
                <div className="flex flex-wrap items-end gap-2">
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Buscar contrato ou cliente</label>
                        <div className="relative">
                            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                value={buscaInput}
                                onChange={(e) => setBuscaInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && aplicarBusca()}
                                placeholder="Nº do contrato ou nome do cliente"
                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                            />
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={aplicarBusca}
                        className="px-4 py-2 text-sm font-semibold rounded-xl bg-gray-900 text-brand-yellow hover:bg-gray-800 transition-colors"
                    >
                        Buscar
                    </button>
                    {buscaInput && (
                        <button
                            type="button"
                            onClick={limparBusca}
                            className="px-4 py-2 text-sm font-semibold rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                            Limpar
                        </button>
                    )}
                </div>
            )}

            {contratos.length === 0 ? (
                <div className="text-sm text-gray-400 py-6 text-center">{emptyMessage}</div>
            ) : (
                <div className="space-y-3">
                    <p className="text-[11px] text-gray-400">
                        {formatNumber(paginacao.total)} contrato(s) no período
                        {paginacao.total_pages > 1 ? ` · página ${paginacao.page + 1} de ${paginacao.total_pages}` : ''}
                    </p>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                                    <th className="py-2 pr-4 font-medium">Contrato</th>
                                    <th className="py-2 pr-4 font-medium">Cliente</th>
                                    <th className="py-2 pr-4 font-medium">Unidades</th>
                                    <th className="py-2 pr-4 font-medium text-right">VGV</th>
                                    <th className="py-2 pr-4 font-medium text-right">Recebido</th>
                                    <th className="py-2 pr-4 font-medium text-right">A Receber</th>
                                    <th className="py-2 pr-4 font-medium text-right">% Recebido</th>
                                </tr>
                            </thead>
                            <tbody>
                                {contratos.map((row) => (
                                    <tr key={row.contrato} className="border-b border-gray-50 hover:bg-gray-50">
                                        <td className="py-2 pr-4 text-gray-700">{row.contrato}</td>
                                        <td className="py-2 pr-4 text-gray-700">{row.cliente}</td>
                                        <td className="py-2 pr-4 text-gray-500">
                                            <span className="font-medium text-gray-700">{formatNumber(row.qtd_unidades)}</span>
                                        </td>
                                        <td className="py-2 pr-4 text-right text-gray-700">{formatCurrency(row.vgv)}</td>
                                        <td className="py-2 pr-4 text-right text-emerald-600">{formatCurrency(row.recebido)}</td>
                                        <td className="py-2 pr-4 text-right text-amber-600">{formatCurrency(row.a_receber)}</td>
                                        <td className="py-2 pr-4 text-right text-gray-700">{formatPercent(row.pct_recebido)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {paginacao.total_pages > 1 && onPageChange && (
                        <div className="flex items-center justify-between pt-1">
                            <span className="text-sm text-gray-500">
                                Página {paginacao.page + 1} de {paginacao.total_pages}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => onPageChange(Math.max(0, paginacao.page - 1))}
                                    disabled={paginacao.page === 0}
                                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronLeftIcon className="w-4 h-4" /> Anterior
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onPageChange(paginacao.page + 1)}
                                    disabled={paginacao.page + 1 >= paginacao.total_pages}
                                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    Próximo <ChevronRightIcon className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default ContratosFinanceiroPanel
export { CONTRATOS_POR_PAGINA }
